import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, DollarSign, Users as UsersIcon, TrendingUp, Shield, Sparkles, Zap, ShieldCheck, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * `/billing` — Admin-only billing & user-plans overview.
 * MRR by tier, lifetime paid, and a per-user table with tier / storage /
 * total paid / purchase count.
 */
export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [sort, setSort] = useState("paid_desc");
  const [compUser, setCompUser] = useState(null);

  useEffect(() => {
    api.get("/admin/billing")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load billing"))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let out = [...data.users];
    if (tierFilter !== "all") out = out.filter((u) => u.subscription_tier === tierFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((u) =>
        (u.email || "").toLowerCase().includes(s) ||
        (u.name || "").toLowerCase().includes(s),
      );
    }
    const cmp = {
      paid_desc: (a, b) => b.total_paid_cents - a.total_paid_cents,
      storage_desc: (a, b) => b.storage_used_bytes - a.storage_used_bytes,
      recent: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      tier: (a, b) => (b.subscription_tier || "").localeCompare(a.subscription_tier || ""),
    }[sort];
    out.sort(cmp);
    return out;
  }, [data, q, tierFilter, sort]);

  if (loading) return <div className="p-12 text-stone-500 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  if (!data) return null;
  const { summary } = data;
  const fmtGB = (b) => (b / (1024 ** 3)).toFixed(b >= 1024 ** 3 ? 1 : 2);
  const currencySym = summary.currency === "cad" ? "CA$" : summary.currency === "usd" ? "$" : summary.currency.toUpperCase() + " ";

  return (
    <div data-testid="admin-billing">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Admin</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Billing &amp; Users</h1>
        <p className="text-stone-400 mt-3">Every user, their plan, storage usage, and lifetime revenue.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <SummaryCard icon={TrendingUp} label="MRR" value={`${currencySym}${summary.mrr_total}`} sub="Monthly recurring" accent />
        <SummaryCard icon={DollarSign} label="Lifetime paid" value={`${currencySym}${summary.total_paid_lifetime.toFixed(2)}`} sub="All time" />
        <SummaryCard icon={UsersIcon} label="Users" value={summary.user_count} sub={`${(summary.active_by_tier.storage || 0) + (summary.active_by_tier.creator || 0) + (summary.active_by_tier.pro || 0)} paying`} />
        <SummaryCard icon={Sparkles} label="Active plans" value={
          <div className="flex items-center gap-2 text-sm">
            <span title="Storage" className="inline-flex items-center gap-1"><Shield size={12} className="text-stone-400" />{summary.active_by_tier.storage || 0}</span>
            <span title="Creator" className="inline-flex items-center gap-1"><Sparkles size={12} className="text-orange-400" />{summary.active_by_tier.creator || 0}</span>
            <span title="Pro" className="inline-flex items-center gap-1"><Zap size={12} className="text-orange-500" />{summary.active_by_tier.pro || 0}</span>
          </div>
        } sub="By tier" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name…"
            className="field-input w-full pl-8 text-sm"
            data-testid="billing-search"
          />
        </div>
        <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="field-input text-sm" data-testid="billing-tier-filter">
          <option value="all">All tiers</option>
          <option value="free">Free</option>
          <option value="storage">Rodeo Storage</option>
          <option value="creator">Rodeo Creator</option>
          <option value="pro">Rodeo Pro</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="field-input text-sm" data-testid="billing-sort">
          <option value="paid_desc">Sort: Top paid</option>
          <option value="storage_desc">Sort: Most storage</option>
          <option value="tier">Sort: Tier</option>
          <option value="recent">Sort: Newest</option>
        </select>
        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-mono-tc ml-auto">
          {rows.length} of {summary.user_count} users
        </span>
      </div>

      {/* Users table */}
      <div className="border border-stone-800 overflow-x-auto" data-testid="billing-users-table">
        <div className="hidden md:grid grid-cols-[2fr_120px_100px_180px_140px_100px_90px] gap-3 px-4 py-2 border-b border-stone-800 text-[10px] uppercase tracking-widest text-stone-500">
          <div>User</div>
          <div>Tier</div>
          <div className="text-right">Purchases</div>
          <div>Storage</div>
          <div className="text-right">Lifetime paid</div>
          <div className="text-right">Last paid</div>
          <div className="text-right">Actions</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-stone-500 text-sm">No users match your filters.</div>
        ) : (
          rows.map((u) => {
            const pct = u.storage_limit_bytes > 0 ? Math.min(100, (u.storage_used_bytes / u.storage_limit_bytes) * 100) : 0;
            const barColor = pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-orange-500";
            return (
              <div
                key={u.id}
                className="grid grid-cols-1 md:grid-cols-[2fr_120px_100px_180px_140px_100px_90px] gap-3 px-4 py-3 border-b border-stone-900 items-center text-sm"
                data-testid={`billing-user-row-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {u.role === "admin" && <ShieldCheck size={13} className="text-orange-500 shrink-0" />}
                    <span className="font-semibold truncate">{u.name || "—"}</span>
                  </div>
                  <div className="text-[11px] text-stone-500 truncate">{u.email}</div>
                  {u.comped && (
                    <div className="text-[9px] uppercase tracking-widest text-amber-400 mt-0.5" title={u.comp_note || "Comped by admin"}>
                      ★ Comped {u.comp_note ? `· ${u.comp_note}` : ""}
                    </div>
                  )}
                </div>
                <div>
                  <TierBadge tier={u.subscription_tier} status={u.subscription_status} cancelAtPeriodEnd={u.cancel_at_period_end} />
                </div>
                <div className="text-right font-mono-tc text-stone-300">{u.purchases}</div>
                <div>
                  {u.storage_limit_bytes > 0 ? (
                    <div>
                      <div className="text-[11px] font-mono-tc text-stone-400 mb-0.5">
                        {fmtGB(u.storage_used_bytes)} / {fmtGB(u.storage_limit_bytes)} GB
                        <span className={`ml-2 ${pct >= 90 ? "text-red-400" : pct >= 75 ? "text-amber-400" : "text-stone-500"}`}>
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-stone-900 border border-stone-800 overflow-hidden">
                        <div className={`h-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ) : (
                    <span className="text-[11px] text-stone-600">Free — no upload</span>
                  )}
                </div>
                <div className="text-right font-mono-tc font-semibold text-orange-400">
                  {currencySym}{u.total_paid.toFixed(2)}
                </div>
                <div className="text-right text-[11px] text-stone-500 font-mono-tc">
                  {u.last_paid ? new Date(u.last_paid).toLocaleDateString() : "—"}
                </div>
                <div className="text-right">
                  {u.role === "admin" ? (
                    <span className="text-[10px] text-stone-600">—</span>
                  ) : u.comped ? (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Revoke comp for ${u.email}? They'll drop to Free immediately.`)) return;
                        try {
                          await api.post("/admin/billing/comp/revoke", { user_id: u.id });
                          toast.success("Comp revoked");
                          setData((d) => ({
                            ...d,
                            users: d.users.map((r) => r.id === u.id
                              ? { ...r, subscription_tier: "free", subscription_status: "", comped: false, comp_note: "" }
                              : r),
                          }));
                        } catch (err) {
                          toast.error("Revoke failed: " + (err?.response?.data?.detail || err.message));
                        }
                      }}
                      className="btn-secondary !py-1 !px-2 text-[10px] hover:!text-rose-400 hover:!border-rose-700"
                      data-testid={`revoke-comp-${u.id}`}
                    >
                      Revoke
                    </button>
                  ) : (
                    <button
                      onClick={() => setCompUser(u)}
                      className="btn-secondary !py-1 !px-2 text-[10px] hover:!text-amber-400 hover:!border-amber-700"
                      data-testid={`comp-user-${u.id}`}
                    >
                      Comp
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {compUser && (
        <CompModal
          user={compUser}
          onClose={() => setCompUser(null)}
          onSaved={(patch) => {
            setCompUser(null);
            setData((d) => ({
              ...d,
              users: d.users.map((r) => r.id === patch.user_id ? { ...r, ...patch.row } : r),
            }));
          }}
        />
      )}
    </div>
  );
}


function CompModal({ user, onClose, onSaved }) {
  const [tier, setTier] = useState("creator");
  const [months, setMonths] = useState(3);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/billing/comp", {
        user_id: user.id,
        tier,
        months,
        note,
      });
      toast.success(`Comped ${user.email} · ${months} month${months === 1 ? "" : "s"} of Rodeo ${tier}`);
      onSaved({
        user_id: user.id,
        row: {
          subscription_tier: tier,
          subscription_status: "active",
          subscription_period_end: r.data.period_end,
          comped: true,
          comp_note: note,
        },
      });
    } catch (err) {
      toast.error("Comp failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="comp-modal"
    >
      <div className="bg-stone-950 border border-amber-700 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-stone-800">
          <div className="label-tiny text-amber-500">Comp free months</div>
          <div className="font-display uppercase text-xl truncate">{user.name || user.email}</div>
          <div className="text-[11px] text-stone-500 truncate">{user.email}</div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="label-tiny mb-1">Tier</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "storage", label: "Storage · $10" },
                { key: "creator", label: "Creator · $20" },
                { key: "pro", label: "Pro · $40" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTier(t.key)}
                  className={`btn-secondary !py-2 text-xs ${tier === t.key ? "!text-amber-400 !border-amber-700" : ""}`}
                  data-testid={`comp-tier-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-tiny mb-1">Months</div>
            <div className="grid grid-cols-5 gap-2">
              {[1, 3, 6, 12, 24].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMonths(m)}
                  className={`btn-secondary !py-2 text-xs ${months === m ? "!text-amber-400 !border-amber-700" : ""}`}
                  data-testid={`comp-months-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-tiny mb-1">Reason / note (optional)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Beta tester · Friend of the family · Comp until Aug"
              className="field-input w-full"
              data-testid="comp-note"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-stone-800 flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="comp-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-primary flex-1 !bg-amber-600 hover:!bg-amber-500 inline-flex items-center justify-center gap-1.5"
            data-testid="comp-save"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            Comp {months} mo · {tier}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`border p-4 ${accent ? "border-orange-700/60 bg-orange-950/20" : "border-stone-800"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className={accent ? "text-orange-400" : "text-stone-500"} />
        <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
      </div>
      <div className="font-display text-3xl font-bold leading-none">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-2 font-mono-tc">{sub}</div>}
    </div>
  );
}

function TierBadge({ tier, status, cancelAtPeriodEnd }) {
  const label = tier === "free" ? "Free" : `Rodeo ${tier[0].toUpperCase()}${tier.slice(1)}`;
  const styles = {
    free: "border-stone-700 text-stone-500",
    storage: "border-stone-600 text-stone-300",
    creator: "border-orange-700 bg-orange-950/40 text-orange-300",
    pro: "border-orange-500 bg-orange-950/60 text-orange-200",
  }[tier || "free"];
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${styles} w-fit`}>
        {label}
      </span>
      {status && status !== "active" && tier !== "free" && (
        <span className="text-[9px] uppercase tracking-widest text-amber-500">{status}</span>
      )}
      {cancelAtPeriodEnd && (
        <span className="text-[9px] uppercase tracking-widest text-red-400">cancelling</span>
      )}
    </div>
  );
}
